import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { currentMonth, formatEur, formatEurCents, isPositiveEuros, sumEuros } from '@/lib/format';
import { useState, type JSX } from 'react';
import { PageChrome } from '@/components/page-chrome';
import { useToast } from '@/components/toast';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardBar, CardFoot, CardHead } from '@/components/ui/card';
import { ChartLegend, HBars, ProportionBar } from '@/components/ui/chart';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { KvList, KvRow } from '@/components/ui/kv';
import { Row4, Split21 } from '@/components/ui/layout-grid';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatMonthLabel, MonthSwitcher, PageHead } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { periodStatusLabel, StatusBadge } from '@/components/ui/status-tag';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';
import { internalFetch, internalSend } from '@/lib/api';

interface BillingPeriod {
  id: string;
  clientId: string;
  clientName: string;
  /** The client's billing is deferred: each close carries the month (D-BILL.2). */
  billingDeferred: boolean;
  month: string;
  status: string;
  gross: string;
  charges: string;
  adjustments: string;
  net: string;
  invoiceNumber: string | null;
  invoiceUrl: string | null;
  /** Invoice total TTC (euros), as issued by Qonto: the amount debited. */
  invoiceTotal: string | null;
  debitScheduledAt: string | null;
  /** Month whose invoice billed this deferred period (status `carried`). */
  carriedInto: string | null;
}
interface BillingResponse {
  month: string;
  periods: BillingPeriod[];
}

/** Credit note to issue by hand in Qonto (lot 18, D-QONTO.6). */
interface PendingCreditNote {
  id: string;
  clientName: string;
  month: string;
  reason: string;
  amount: string;
  invoiceNumber: string | null;
}

type CloseOutcome =
  | { status: 'invoiced'; invoiceNumber: string }
  | { status: 'empty' }
  | { status: 'deferred' }
  | { status: 'carried' }
  | { status: 'missing_billing_details' }
  | { status: 'already_closed' };

interface BatchRow {
  id: string;
  provider: string;
  modelId: string;
  itemCount: number;
  createdAt: string;
}

type Dialog =
  | { type: 'month' }
  | { type: 'closeClient'; clientId: string; clientName: string; deferred: boolean };

type BillingTab = 'clients' | 'creditNotes' | 'batches';

/** French message for the outcome of a client close. */
function closeOutcomeMessage(outcome: CloseOutcome): string {
  switch (outcome.status) {
    case 'invoiced':
      return `Facture ${outcome.invoiceNumber} émise`;
    case 'empty':
      return 'Rien à facturer : période vide';
    case 'deferred':
      return 'Facturation reportée au mois suivant';
    case 'carried':
      return 'Montant sous 0,50 € : reporté au mois suivant';
    case 'missing_billing_details':
      return 'Adresse ou SIREN manquant : complétez la fiche client';
    case 'already_closed':
      return 'Période déjà clôturée';
  }
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count > 1 ? pluralForm : singular}`;
}

function isClosable(period: BillingPeriod): boolean {
  return period.status === 'open' || period.status === 'closing';
}

/** The deferral can be switched while the month is not being invoiced yet. */
function canToggleDeferral(period: BillingPeriod): boolean {
  return period.status === 'open' || period.status === 'deferred';
}

export function BillingPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [tab, setTab] = useState<BillingTab>('clients');

  const billing = useQuery({
    queryKey: ['billing', month],
    queryFn: () => internalFetch<BillingResponse>(`/internal/billing?month=${month}`),
  });
  const creditNotes = useQuery({
    queryKey: ['credit-notes'],
    queryFn: () =>
      internalFetch<{ creditNotes: PendingCreditNote[] }>('/internal/billing/credit-notes'),
  });
  const batches = useQuery({
    queryKey: ['batches'],
    queryFn: () => internalFetch<{ batches: BatchRow[] }>('/internal/batches'),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['billing', month] });
  };

  const closeMonth = useMutation({
    mutationFn: () => internalSend('POST', '/internal/billing/close-month', { month }),
    onSuccess: () => {
      invalidate();
      setDialog(null);
      notify(`Mois de ${formatMonthLabel(month)} clôturé`);
    },
  });
  const markCreditNoteIssued = useMutation({
    mutationFn: (id: string) => internalSend('POST', `/internal/billing/credit-notes/${id}/issued`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      notify('Avoir marqué émis');
    },
  });
  const closeClient = useMutation({
    mutationFn: (clientId: string) =>
      internalSend<CloseOutcome>('POST', `/internal/clients/${clientId}/close`, { month }),
    onSuccess: (outcome) => {
      invalidate();
      setDialog(null);
      notify(closeOutcomeMessage(outcome));
    },
  });

  const setDeferral = useMutation({
    mutationFn: ({ clientId, deferred }: { clientId: string; deferred: boolean }) =>
      internalSend('PUT', `/internal/clients/${clientId}/billing-deferral`, { deferred }),
    onSuccess: (_, { deferred }) => {
      invalidate();
      notify(
        deferred
          ? 'Facturation reportée : chaque clôture passe au mois suivant'
          : 'Report levé : la prochaine clôture facture tous les mois reportés',
      );
    },
  });

  const periods = billing.data?.periods ?? [];
  const pendingCreditNotes = creditNotes.data?.creditNotes ?? [];
  const batchRows = batches.data?.batches ?? [];
  const monthLabel = formatMonthLabel(month);

  const totals = {
    gross: sumEuros(periods.map((p) => p.gross)),
    charges: sumEuros(periods.map((p) => p.charges)),
    adjustments: sumEuros(periods.map((p) => p.adjustments)),
    net: sumEuros(periods.map((p) => p.net)),
  };
  const paidPeriods = periods.filter((p) => p.status === 'paid');
  const openPeriods = periods.filter(isClosable);
  const batchItems = batchRows.reduce((total, batch) => total + batch.itemCount, 0);
  const statValue = (value: string): string => (billing.isLoading ? '—' : value);

  const usageItems = [...periods]
    .sort((a, b) => Number(b.gross) - Number(a.gross))
    .map((p) => ({
      key: p.id,
      name: p.clientName,
      value: Number(p.gross),
      label: formatEurCents(p.gross),
    }));
  const usageAria =
    usageItems.length === 0
      ? `Aucun usage IA refacturé en ${monthLabel}.`
      : `Usage IA refacturé : ${usageItems.map((i) => `${i.name} ${i.label}`).join(', ')}.`;
  const compositionAria = `Le net de ${formatEurCents(totals.net)} se compose de ${formatEurCents(
    totals.charges,
  )} d'abonnements et ${formatEurCents(totals.gross)} d'usage IA, moins ${formatEurCents(
    totals.adjustments,
  )} d'ajustements.`;

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Facturation' }]}
        actions={
          <>
            <MonthSwitcher month={month} onChange={setMonth} />
            <Button
              type="button"
              onClick={() => {
                setDialog({ type: 'month' });
              }}
            >
              Clôturer le mois
            </Button>
          </>
        }
      />
      <PageHead
        title="Facturation"
        subtitle="Clôture mensuelle : facture Qonto, prélèvement SEPA 24 h après. Montants HT. Chaque clôture demande une confirmation. Un client en report passe au mois suivant, sans limite, jusqu’à la levée du report."
      />

      {pendingCreditNotes.length > 0 ? (
        <Callout
          variant="info"
          title={`${plural(pendingCreditNotes.length, 'avoir', 'avoirs')} à émettre dans Qonto`}
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setTab('creditNotes');
              }}
            >
              Voir les avoirs
            </Button>
          }
        >
          Émettez-les à la main dans Qonto, puis marquez-les émis.
        </Callout>
      ) : null}

      <Row4 aria-label="Indicateurs de facturation">
        <StatCard
          label="Net du mois"
          value={statValue(formatEurCents(totals.net))}
          foot={plural(periods.length, 'client', 'clients')}
        />
        <StatCard
          label="Déjà réglé"
          value={statValue(formatEurCents(sumEuros(paidPeriods.map((p) => p.net))))}
          foot={`${plural(paidPeriods.length, 'client', 'clients')} sur ${periods.length}`}
        />
        <StatCard
          label="Reste à clôturer"
          value={statValue(formatEurCents(sumEuros(openPeriods.map((p) => p.net))))}
          foot={plural(openPeriods.length, 'client ouvert', 'clients ouverts')}
        />
        <StatCard
          label="Lots en cours"
          value={batches.isLoading ? '—' : String(batchRows.length)}
          foot={
            batchRows.length === 0
              ? 'aucun lot en cours'
              : plural(batchItems, 'élément', 'éléments')
          }
        />
      </Row4>

      <Split21 className="items-start">
        <Card>
          <CardHead
            title="Usage IA refacturé par client"
            description={`${monthLabel}, hors abonnements, en € HT`}
          />
          <CardContent className="grid gap-3">
            {billing.isLoading ? (
              <LoadingState />
            ) : usageItems.length === 0 ? (
              <EmptyState title="Aucune période pour ce mois" />
            ) : (
              <HBars items={usageItems} ariaLabel={usageAria} color="chart-2" />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHead
            title="Composition du net"
            description="Abonnements, usage IA et ajustements, en € HT"
          />
          <CardContent className="grid gap-3">
            {billing.isLoading ? (
              <LoadingState />
            ) : (
              <>
                <ProportionBar
                  ariaLabel={compositionAria}
                  segments={[
                    { key: 'charges', value: Number(totals.charges), color: 'chart-1' },
                    { key: 'gross', value: Number(totals.gross), color: 'chart-2' },
                  ]}
                />
                <KvList>
                  <KvRow
                    label={<ChartLegend items={[{ label: 'Abonnements', color: 'chart-1' }]} />}
                    mono
                  >
                    {formatEurCents(totals.charges)}
                  </KvRow>
                  <KvRow
                    label={<ChartLegend items={[{ label: 'Usage IA', color: 'chart-2' }]} />}
                    mono
                  >
                    {formatEurCents(totals.gross)}
                  </KvRow>
                  <KvRow label="Ajustements (crédits)" mono>
                    {isPositiveEuros(totals.adjustments) ? '-' : ''}
                    {formatEurCents(totals.adjustments)}
                  </KvRow>
                </KvList>
              </>
            )}
          </CardContent>
        </Card>
      </Split21>

      <Card>
        <CardBar
          start={
            <TableTabs
              aria-label="Clôture"
              value={tab}
              onValueChange={setTab}
              items={[
                { value: 'clients', label: 'Clôture par client', count: periods.length },
                {
                  value: 'creditNotes',
                  label: 'Avoirs à émettre',
                  count: pendingCreditNotes.length,
                },
                { value: 'batches', label: 'Lots en cours', count: batchRows.length },
              ]}
            />
          }
        />

        {tab === 'clients' ? (
          <>
            <Table className="min-w-[980px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead kind="num">Usage</TableHead>
                  <TableHead kind="num">Abonnements</TableHead>
                  <TableHead kind="num">Ajustements</TableHead>
                  <TableHead kind="num">Net HT</TableHead>
                  <TableHead>Facture</TableHead>
                  <TableHead>Prélèvement</TableHead>
                  <TableHead kind="act" className="w-[240px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {periods.map((period) => (
                  <TableRow key={period.id}>
                    <TableCell kind="name">
                      <span className="inline-flex items-center gap-2">
                        {period.clientName}
                        {period.billingDeferred ? <Tag>Report</Tag> : null}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={periodStatusLabel(period.status)} />
                    </TableCell>
                    <TableCell kind="num">{formatEur(period.gross)}</TableCell>
                    <TableCell kind="num">{formatEur(period.charges)}</TableCell>
                    <TableCell kind="num">{formatEur(period.adjustments)}</TableCell>
                    <TableCell kind="num">{formatEur(period.net)}</TableCell>
                    <TableCell>
                      <InvoiceCell period={period} />
                    </TableCell>
                    <TableCell kind="sub">{debitLabel(period)}</TableCell>
                    <TableCell kind="act" className="w-[240px]">
                      {canToggleDeferral(period) ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={setDeferral.isPending}
                          onClick={() => {
                            setDeferral.mutate({
                              clientId: period.clientId,
                              deferred: !period.billingDeferred,
                            });
                          }}
                        >
                          {period.billingDeferred ? 'Ne plus reporter' : 'Reporter'}
                        </Button>
                      ) : null}
                      {isClosable(period) ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDialog({
                              type: 'closeClient',
                              clientId: period.clientId,
                              clientName: period.clientName,
                              deferred: period.billingDeferred,
                            });
                          }}
                        >
                          {period.status === 'closing' ? 'Reprendre' : 'Clôturer'}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                {billing.isLoading ? (
                  <TableEmptyRow colSpan={9}>Chargement…</TableEmptyRow>
                ) : periods.length === 0 ? (
                  <TableEmptyRow colSpan={9}>Aucune période pour ce mois.</TableEmptyRow>
                ) : null}
              </TableBody>
              {periods.length > 0 ? (
                <TableFooter>
                  <TableRow>
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell kind="num">{formatEur(totals.gross)}</TableCell>
                    <TableCell kind="num">{formatEur(totals.charges)}</TableCell>
                    <TableCell kind="num">{formatEur(totals.adjustments)}</TableCell>
                    <TableCell kind="num">{formatEur(totals.net)}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
            <CardFoot
              start={`${plural(periods.length, 'client', 'clients')} en ${monthLabel}`}
              end="Net HT = usage + abonnements - ajustements"
            />
          </>
        ) : null}

        {tab === 'creditNotes' ? (
          creditNotes.isLoading ? (
            <LoadingState />
          ) : pendingCreditNotes.length === 0 ? (
            <EmptyState
              title="Aucun avoir en attente"
              description="Un geste commercial sur un mois déjà facturé apparaît ici : émettez l’avoir à la main dans Qonto, puis marquez-le émis."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Facture</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead kind="num">Montant HT</TableHead>
                    <TableHead kind="act" className="w-[140px]">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingCreditNotes.map((note) => (
                    <TableRow key={note.id}>
                      <TableCell kind="name">{note.clientName}</TableCell>
                      <TableCell kind="mono">
                        {note.invoiceNumber ?? formatMonthLabel(note.month)}
                      </TableCell>
                      <TableCell kind="sub">{note.reason}</TableCell>
                      <TableCell kind="num">{formatEur(note.amount)}</TableCell>
                      <TableCell kind="act" className="w-[140px]">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={markCreditNoteIssued.isPending}
                          onClick={() => {
                            markCreditNoteIssued.mutate(note.id);
                          }}
                        >
                          Marquer émis
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CardFoot
                start={`${plural(pendingCreditNotes.length, 'avoir', 'avoirs')} à émettre dans Qonto`}
                end="Montants HT"
              />
            </>
          )
        ) : null}

        {tab === 'batches' ? (
          batches.isLoading ? (
            <LoadingState />
          ) : batchRows.length === 0 ? (
            <EmptyState
              title="Aucun lot en cours"
              description="Les lots apparaissent ici pendant la clôture mensuelle."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead>Modèle</TableHead>
                  <TableHead kind="num">Éléments</TableHead>
                  <TableHead>Créé le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchRows.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      <Tag>{batch.provider}</Tag>
                    </TableCell>
                    <TableCell kind="mono">{batch.modelId}</TableCell>
                    <TableCell kind="num">{batch.itemCount}</TableCell>
                    <TableCell kind="mono">
                      {new Date(batch.createdAt).toLocaleString('fr-FR', {
                        timeZone: 'Europe/Paris',
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}
      </Card>

      <ConfirmDialog
        open={dialog?.type === 'month'}
        onClose={() => {
          setDialog(null);
        }}
        onConfirm={() => {
          closeMonth.mutate();
        }}
        title={`Clôturer ${monthLabel} ?`}
        subtitle="Action irréversible."
        confirmLabel="Clôturer le mois"
        busy={closeMonth.isPending}
        variant="destructive"
        note="Une facture Qonto est émise et envoyée à chaque client ouvert ; ceux qui ont un mandat SEPA sont prélevés 24 h plus tard."
      />
      <ConfirmDialog
        open={dialog?.type === 'closeClient'}
        onClose={() => {
          setDialog(null);
        }}
        onConfirm={() => {
          if (dialog?.type === 'closeClient') {
            closeClient.mutate(dialog.clientId);
          }
        }}
        title={dialog?.type === 'closeClient' ? `Clôturer ${dialog.clientName} ?` : 'Clôturer ?'}
        subtitle={`Période ${monthLabel}`}
        confirmLabel="Clôturer"
        busy={closeClient.isPending}
        variant="destructive"
        note={
          dialog?.type === 'closeClient' && dialog.deferred
            ? 'Facturation en report : aucune facture n’est émise, le mois passe au mois suivant.'
            : 'La facture Qonto est émise et envoyée au client, avec les mois reportés s’il y en a ; avec un mandat SEPA, il est prélevé 24 h plus tard.'
        }
      />
    </>
  );
}

function InvoiceCell({ period }: { period: BillingPeriod }): JSX.Element {
  if (period.carriedInto !== null) {
    return (
      <span className="text-muted-foreground">
        Avec la facture de {formatMonthLabel(period.carriedInto)}
      </span>
    );
  }
  if (period.invoiceNumber === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const primary =
    period.invoiceUrl !== null ? (
      <a
        href={period.invoiceUrl}
        target="_blank"
        rel="noreferrer"
        className="rounded-sm underline underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {period.invoiceNumber}
      </a>
    ) : (
      period.invoiceNumber
    );
  return (
    <Cell2
      primary={primary}
      primaryMono
      secondary={period.invoiceTotal !== null ? `${formatEur(period.invoiceTotal)} TTC` : undefined}
      secondaryMono
    />
  );
}

/** When the SEPA debit runs (or ran) for a period; « Virement attendu » without a mandate. */
function debitLabel(period: BillingPeriod): string {
  if (period.debitScheduledAt === null) {
    return period.status === 'invoiced' ? 'Virement attendu' : '—';
  }
  const at = new Date(period.debitScheduledAt).toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris',
    dateStyle: 'short',
    timeStyle: 'short',
  });
  return period.status === 'invoiced' ? `Prévu le ${at}` : at;
}
